import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  Plus, Save, Loader2, GripVertical, Trash2, ChevronDown, ChevronRight,
  TrendingUp, Percent, DollarSign, Target, CircleDot, Info,
  BookMarked, Download, Library,
} from 'lucide-react';
import {
  MarginRule,
  MarginType,
  RoundingMode,
  TargetScope,
  ExportTemplateColumn,
  MARGIN_TYPE_INFO,
  ROUNDING_MODE_INFO,
  ROUNDING_UNIT_PRESETS,
  TARGET_SCOPE_INFO,
  NUMERIC_SOURCE_FIELDS,
  OtherTemplateRulesGroup,
} from './types';
import { describeMarginRule } from './transforms';

// ─── Preset Type ────────────────────────────────────────────────────

interface MarginRulePreset {
  id: string;
  rule_name: string;
  margin_type: string;
  margin_value: number;
  rounding_mode: string;
  rounding_unit: number;
  target_scope: string;
  target_config: Record<string, any>;
  applies_to_field: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

// ─── Props ──────────────────────────────────────────────────────────

interface MarginRulesManagerProps {
  templateId: string;
  templateColumns: ExportTemplateColumn[];
  onRulesChange?: (rules: MarginRule[]) => void;
  /** Callback to expose margin rules from other templates for reuse in field pickers */
  onOtherTemplateRulesChange?: (groups: OtherTemplateRulesGroup[]) => void;
}


// ─── Component ──────────────────────────────────────────────────────

const MarginRulesManager: React.FC<MarginRulesManagerProps> = ({
  templateId,
  templateColumns,
  onRulesChange,
  onOtherTemplateRulesChange,
}) => {

  const [rules, setRules] = useState<MarginRule[]>([]);

  const [otherTemplateRules, setOtherTemplateRules] = useState<{ templateName: string; templateId: string; rules: MarginRule[] }[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showOtherRules, setShowOtherRules] = useState(false);

  // ─── Preset State ─────────────────────────────────────────────────
  const [presets, setPresets] = useState<MarginRulePreset[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [showPresetPicker, setShowPresetPicker] = useState(false);
  const [savingPresetRuleId, setSavingPresetRuleId] = useState<string | null>(null);
  const [presetSaveName, setPresetSaveName] = useState('');
  const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);

  // Build combined field options: numeric source fields + calculated template column names
  const fieldOptions = [
    ...NUMERIC_SOURCE_FIELDS.map(f => ({ field: f.field, label: f.label, group: 'Source Fields' })),
    ...templateColumns
      .filter(c => c.source_type === 'calculated' && c.transform_config?.calcType === 'math')
      .map(c => ({ field: c.export_column_name, label: `${c.export_column_name} (calc)`, group: 'Calculated Columns' })),
  ];

  // ─── Load Presets ─────────────────────────────────────────────────
  const loadPresets = useCallback(async () => {
    setPresetsLoading(true);
    try {
      const { data, error } = await supabase
        .from('margin_rule_presets')
        .select('*')
        .order('rule_name');
      if (error) throw error;
      setPresets((data || []) as MarginRulePreset[]);
    } catch (err: any) {
      console.error('Failed to load presets:', err);
    } finally {
      setPresetsLoading(false);
    }
  }, []);

  useEffect(() => { loadPresets(); }, [loadPresets]);

  // ─── Load Rules ─────────────────────────────────────────────────
  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      // Load rules for current template
      const { data, error } = await supabase
        .from('export_template_margin_rules')
        .select('*')
        .eq('template_id', templateId)
        .order('sort_order');
      if (error) throw error;
      const loaded = (data || []) as MarginRule[];
      setRules(loaded);
      onRulesChange?.(loaded);

      // Load rules from ALL other templates
      const { data: allRules, error: allErr } = await supabase
        .from('export_template_margin_rules')
        .select('*')
        .neq('template_id', templateId)
        .order('sort_order');
      if (!allErr && allRules) {
        // Group by template_id, then fetch template names
        const byTemplate = new Map<string, MarginRule[]>();
        for (const r of allRules as MarginRule[]) {
          const existing = byTemplate.get(r.template_id) || [];
          existing.push(r);
          byTemplate.set(r.template_id, existing);
        }
        // Fetch template names
        const templateIds = Array.from(byTemplate.keys());
        if (templateIds.length > 0) {
          const { data: templates } = await supabase
            .from('export_templates')
            .select('id, template_name')
            .in('id', templateIds);
          const nameMap = new Map<string, string>();
          for (const t of (templates || [])) {
            nameMap.set(t.id, t.template_name);
          }
          const grouped = templateIds.map(tid => ({
            templateId: tid,
            templateName: nameMap.get(tid) || 'Unknown Template',
            rules: byTemplate.get(tid) || [],
          })).sort((a, b) => a.templateName.localeCompare(b.templateName));
          setOtherTemplateRules(grouped);
          onOtherTemplateRulesChange?.(grouped);
        } else {
          setOtherTemplateRules([]);
          onOtherTemplateRulesChange?.([]);
        }
      }

    } catch (err: any) {
      toast({ title: 'Error', description: 'Failed to load margin rules', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [templateId, onRulesChange]);

  useEffect(() => { loadRules(); }, [loadRules]);


  // ─── Add Rule ───────────────────────────────────────────────────
  const handleAddRule = async () => {
    const nextOrder = rules.length > 0 ? Math.max(...rules.map(r => r.sort_order)) + 1 : 1;
    setSaving(true);
    try {
      const { data, error } = await supabase.from('export_template_margin_rules')
        .insert({
          template_id: templateId,
          rule_name: `Margin Rule ${nextOrder}`,
          is_active: true,
          applies_to_field: 'grand_total',
          margin_type: 'percentage',
          margin_value: 10,
          rounding_mode: 'none',
          rounding_unit: 1,
          target_scope: 'all_rows',
          target_config: {},
          sort_order: nextOrder,
          created_by: 'admin',
        })
        .select().single();
      if (error) throw error;
      const newRules = [...rules, data as MarginRule];
      setRules(newRules);
      onRulesChange?.(newRules);
      setExpandedRuleId(data.id);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to add rule', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  // ─── Add Rule from Preset ──────────────────────────────────────
  const handleAddFromPreset = async (preset: MarginRulePreset) => {
    const nextOrder = rules.length > 0 ? Math.max(...rules.map(r => r.sort_order)) + 1 : 1;
    setSaving(true);
    try {
      const { data, error } = await supabase.from('export_template_margin_rules')
        .insert({
          template_id: templateId,
          rule_name: preset.rule_name,
          is_active: true,
          applies_to_field: preset.applies_to_field,
          margin_type: preset.margin_type,
          margin_value: preset.margin_value,
          rounding_mode: preset.rounding_mode,
          rounding_unit: preset.rounding_unit,
          target_scope: preset.target_scope,
          target_config: preset.target_config || {},
          sort_order: nextOrder,
          notes: preset.notes,
          created_by: 'admin',
        })
        .select().single();
      if (error) throw error;
      const newRules = [...rules, data as MarginRule];
      setRules(newRules);
      onRulesChange?.(newRules);
      setExpandedRuleId(data.id);
      setShowPresetPicker(false);
      toast({ title: 'Loaded Preset', description: `Margin rule "${preset.rule_name}" loaded from preset library` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to add rule from preset', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  // ─── Save Rule as Preset ──────────────────────────────────────
  const handleSaveAsPreset = async () => {
    if (!savingPresetRuleId || !presetSaveName.trim()) return;
    const rule = rules.find(r => r.id === savingPresetRuleId);
    if (!rule) return;

    setSaving(true);
    try {
      const { error } = await supabase.from('margin_rule_presets').insert({
        rule_name: presetSaveName.trim(),
        margin_type: rule.margin_type,
        margin_value: rule.margin_value,
        rounding_mode: rule.rounding_mode,
        rounding_unit: rule.rounding_unit,
        target_scope: rule.target_scope,
        target_config: rule.target_config || {},
        applies_to_field: rule.applies_to_field,
        notes: rule.notes,
        created_by: 'admin',
      });
      if (error) throw error;
      toast({ title: 'Preset Saved', description: `"${presetSaveName}" saved to preset library` });
      setShowSavePresetDialog(false);
      setSavingPresetRuleId(null);
      setPresetSaveName('');
      await loadPresets();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save preset', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  // ─── Delete Preset ────────────────────────────────────────────
  const handleDeletePreset = async (presetId: string) => {
    try {
      const { error } = await supabase.from('margin_rule_presets').delete().eq('id', presetId);
      if (error) throw error;
      setPresets(prev => prev.filter(p => p.id !== presetId));
      toast({ title: 'Deleted', description: 'Preset removed from library' });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to delete preset', variant: 'destructive' });
    }
  };

  // ─── Update Rule ────────────────────────────────────────────────
  const handleUpdateRule = async (ruleId: string, updates: Partial<MarginRule>) => {
    setSaving(true);
    try {
      const { error } = await supabase.from('export_template_margin_rules')
        .update({ ...updates, updated_at: new Date().toISOString(), updated_by: 'admin' })
        .eq('id', ruleId);
      if (error) throw error;
      const newRules = rules.map(r => r.id === ruleId ? { ...r, ...updates } : r);
      setRules(newRules);
      onRulesChange?.(newRules);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to update rule', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  // ─── Delete Rule ────────────────────────────────────────────────
  const handleDeleteRule = async (ruleId: string) => {
    setSaving(true);
    try {
      const { error } = await supabase.from('export_template_margin_rules').delete().eq('id', ruleId);
      if (error) throw error;
      const newRules = rules.filter(r => r.id !== ruleId);
      setRules(newRules);
      onRulesChange?.(newRules);
      if (expandedRuleId === ruleId) setExpandedRuleId(null);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to delete rule', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  // ─── Reorder ────────────────────────────────────────────────────
  const handleMoveRule = async (ruleId: string, direction: 'up' | 'down') => {
    const idx = rules.findIndex(r => r.id === ruleId);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= rules.length) return;

    const reordered = [...rules];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    const updated = reordered.map((r, i) => ({ ...r, sort_order: i + 1 }));
    setRules(updated);
    onRulesChange?.(updated);

    try {
      await Promise.all(updated.map(r =>
        supabase.from('export_template_margin_rules')
          .update({ sort_order: r.sort_order, updated_at: new Date().toISOString() })
          .eq('id', r.id)
      ));
    } catch {
      toast({ title: 'Error', description: 'Failed to save rule order', variant: 'destructive' });
    }
  };

  // Local state updater (for immediate UI feedback before save)
  const updateLocalRule = (ruleId: string, updates: Partial<MarginRule>) => {
    setRules(prev => prev.map(r => r.id === ruleId ? { ...r, ...updates } : r));
  };

  const activeCount = rules.filter(r => r.is_active).length;

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2 pt-3 px-4 cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <CardTitle className="text-sm font-semibold">Margin Rules</CardTitle>
            {rules.length > 0 && (
              <Badge className="bg-emerald-900/40 text-emerald-400 border-emerald-700/50 text-[10px] px-1.5 py-0">
                {activeCount} active / {rules.length} total
              </Badge>
            )}
            {presets.length > 0 && (
              <Badge className="bg-violet-900/30 text-violet-400 border-violet-700/40 text-[9px] px-1 py-0">
                <Library className="w-2.5 h-2.5 mr-0.5" />
                {presets.length} preset{presets.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          {collapsed ? <ChevronRight className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </CardHeader>

      {!collapsed && (
        <CardContent className="px-4 pb-3 pt-0">
          {/* Info Banner */}
          <div className="flex items-start gap-1.5 text-[9px] text-muted-foreground/70 bg-secondary/30 border border-border/40 rounded px-2 py-1.5 mb-2">
            <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
            <span>
              Margin rules apply pricing adjustments to numeric fields. Rules are evaluated in order — first matching active rule wins per field.
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading rules...
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-xs">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-20" />
              <p>No margin rules defined.</p>
              <p className="text-[10px] mt-1 opacity-60">Add rules to apply pricing adjustments to export values.</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1">
              {rules.map((rule, idx) => {
                const isExpanded = expandedRuleId === rule.id;
                return (
                  <div
                    key={rule.id}
                    className={`border rounded-md transition-colors ${isExpanded ? 'border-emerald-500/40 bg-emerald-900/5' : 'border-border/60 hover:border-border'} ${!rule.is_active ? 'opacity-50' : ''}`}
                  >
                    {/* Rule Header */}
                    <div className="flex items-center gap-1.5 px-2 py-1.5 cursor-pointer" onClick={() => setExpandedRuleId(isExpanded ? null : rule.id)}>
                      <GripVertical className="w-3 h-3 text-muted-foreground/40 flex-shrink-0" />
                      <span className="text-[10px] text-muted-foreground/60 w-4 text-center flex-shrink-0">{rule.sort_order}</span>
                      <span className="text-xs font-medium text-foreground truncate flex-1 min-w-0">{rule.rule_name}</span>
                      {rule.margin_type === 'percentage' ? (
                        <Badge className="bg-blue-900/40 text-blue-400 border-blue-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                          <Percent className="w-2.5 h-2.5 mr-0.5" />
                          {rule.margin_value >= 0 ? '+' : ''}{rule.margin_value}%
                        </Badge>
                      ) : (
                        <Badge className="bg-green-900/40 text-green-400 border-green-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                          <DollarSign className="w-2.5 h-2.5 mr-0.5" />
                          {rule.margin_value >= 0 ? '+' : ''}${rule.margin_value}
                        </Badge>
                      )}
                      {rule.target_scope !== 'all_rows' && (
                        <Badge className="bg-orange-900/40 text-orange-400 border-orange-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                          <Target className="w-2.5 h-2.5 mr-0.5" />
                          {rule.target_config?.value || rule.target_scope}
                        </Badge>
                      )}
                      {rule.rounding_mode !== 'none' && (
                        <Badge className="bg-purple-900/40 text-purple-400 border-purple-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                          <CircleDot className="w-2.5 h-2.5 mr-0.5" />
                          ${rule.rounding_unit}
                        </Badge>
                      )}
                      {isExpanded ? <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                    </div>

                    {/* Rule Summary (collapsed) */}
                    {!isExpanded && (
                      <div className="px-2 pb-1.5 -mt-0.5">
                        <p className="text-[9px] text-muted-foreground/60 truncate">{describeMarginRule(rule)}</p>
                      </div>
                    )}

                    {/* Expanded Editor */}
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-border/40 space-y-2.5">
                        {/* Rule Name */}
                        <div>
                          <Label className="text-[10px] text-muted-foreground uppercase">Rule Name</Label>
                          <Input
                            value={rule.rule_name}
                            onChange={e => updateLocalRule(rule.id, { rule_name: e.target.value })}
                            onBlur={() => handleUpdateRule(rule.id, { rule_name: rule.rule_name })}
                            className="dark-input text-xs h-7"
                          />
                        </div>

                        {/* Applies To Field */}
                        <div>
                          <Label className="text-[10px] text-muted-foreground uppercase">Applies To Field</Label>
                          <Select
                            value={rule.applies_to_field}
                            onValueChange={(v) => {
                              updateLocalRule(rule.id, { applies_to_field: v });
                              handleUpdateRule(rule.id, { applies_to_field: v });
                            }}
                          >
                            <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {fieldOptions.map(f => (
                                <SelectItem key={f.field} value={f.field}>
                                  <span className="flex items-center gap-2">
                                    <span>{f.label}</span>
                                    <span className="text-[9px] text-muted-foreground/50">{f.field}</span>
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Margin Type + Value */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] text-muted-foreground uppercase">Margin Type</Label>
                            <Select
                              value={rule.margin_type}
                              onValueChange={(v: string) => {
                                const mt = v as MarginType;
                                updateLocalRule(rule.id, { margin_type: mt });
                                handleUpdateRule(rule.id, { margin_type: mt });
                              }}
                            >
                              <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(Object.keys(MARGIN_TYPE_INFO) as MarginType[]).map(mt => (
                                  <SelectItem key={mt} value={mt}>
                                    <span className="flex items-center gap-1.5">
                                      {mt === 'percentage' ? <Percent className="w-3 h-3" /> : <DollarSign className="w-3 h-3" />}
                                      {MARGIN_TYPE_INFO[mt].label}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground uppercase">
                              Value {rule.margin_type === 'percentage' ? '(%)' : '($)'}
                            </Label>
                            <Input
                              type="number"
                              step={rule.margin_type === 'percentage' ? '1' : '0.01'}
                              value={rule.margin_value}
                              onChange={e => updateLocalRule(rule.id, { margin_value: parseFloat(e.target.value) || 0 })}
                              onBlur={() => handleUpdateRule(rule.id, { margin_value: rule.margin_value })}
                              className="dark-input text-xs h-7"
                            />
                          </div>
                        </div>

                        {/* Rounding */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] text-muted-foreground uppercase">Rounding Mode</Label>
                            <Select
                              value={rule.rounding_mode}
                              onValueChange={(v: string) => {
                                const rm = v as RoundingMode;
                                updateLocalRule(rule.id, { rounding_mode: rm });
                                handleUpdateRule(rule.id, { rounding_mode: rm });
                              }}
                            >
                              <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(Object.keys(ROUNDING_MODE_INFO) as RoundingMode[]).map(rm => (
                                  <SelectItem key={rm} value={rm}>
                                    {ROUNDING_MODE_INFO[rm].label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {rule.rounding_mode !== 'none' && (
                            <div>
                              <Label className="text-[10px] text-muted-foreground uppercase">Rounding Unit</Label>
                              <Select
                                value={String(rule.rounding_unit)}
                                onValueChange={(v) => {
                                  const unit = parseFloat(v) || 1;
                                  updateLocalRule(rule.id, { rounding_unit: unit });
                                  handleUpdateRule(rule.id, { rounding_unit: unit });
                                }}
                              >
                                <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {ROUNDING_UNIT_PRESETS.map(p => (
                                    <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>

                        {/* Target Scope */}
                        <div>
                          <Label className="text-[10px] text-muted-foreground uppercase">Target Scope</Label>
                          <Select
                            value={rule.target_scope}
                            onValueChange={(v: string) => {
                              const ts = v as TargetScope;
                              const updates: Partial<MarginRule> = { target_scope: ts };
                              if (!TARGET_SCOPE_INFO[ts].needsValue) {
                                updates.target_config = {};
                              }
                              updateLocalRule(rule.id, updates);
                              handleUpdateRule(rule.id, updates);
                            }}
                          >
                            <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {(Object.keys(TARGET_SCOPE_INFO) as TargetScope[]).map(ts => (
                                <SelectItem key={ts} value={ts}>
                                  <span className="flex items-center gap-2">
                                    <span>{TARGET_SCOPE_INFO[ts].label}</span>
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                            {TARGET_SCOPE_INFO[rule.target_scope as TargetScope]?.description || ''}
                          </p>
                        </div>

                        {/* Target Value (if needed) */}
                        {TARGET_SCOPE_INFO[rule.target_scope as TargetScope]?.needsValue && (
                          <div>
                            <Label className="text-[10px] text-muted-foreground uppercase">
                              Target Value
                              {rule.target_scope === 'account_series' && ' (e.g. 6)'}
                              {rule.target_scope === 'account_id' && ' (e.g. 6110)'}
                              {rule.target_scope === 'project' && ' (e.g. ACC)'}
                            </Label>
                            <Input
                              value={rule.target_config?.value || ''}
                              onChange={e => updateLocalRule(rule.id, { target_config: { value: e.target.value } })}
                              onBlur={() => handleUpdateRule(rule.id, { target_config: rule.target_config })}
                              placeholder="Enter target value..."
                              className="dark-input text-xs h-7"
                            />
                          </div>
                        )}

                        {/* Rule Summary Preview */}
                        <div className="bg-secondary/30 border border-border/40 rounded px-2 py-1.5">
                          <p className="text-[9px] text-muted-foreground/70 mb-0.5">Rule Summary</p>
                          <p className="text-[10px] text-foreground font-medium">{describeMarginRule(rule)}</p>
                          {/* Example calculation */}
                          {(() => {
                            const exBase = 25;
                            let adjusted = exBase;
                            if (rule.margin_type === 'percentage') {
                              adjusted = exBase * (1 + rule.margin_value / 100);
                            } else {
                              adjusted = exBase + rule.margin_value;
                            }
                            let final = adjusted;
                            if (rule.rounding_mode !== 'none' && rule.rounding_unit > 0) {
                              if (rule.rounding_mode === 'nearest') final = Math.round(adjusted / rule.rounding_unit) * rule.rounding_unit;
                              else if (rule.rounding_mode === 'up') final = Math.ceil(adjusted / rule.rounding_unit) * rule.rounding_unit;
                              else if (rule.rounding_mode === 'down') final = Math.floor(adjusted / rule.rounding_unit) * rule.rounding_unit;
                            }
                            return (
                              <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                                Example: ${exBase.toFixed(2)} → ${adjusted.toFixed(2)}
                                {rule.rounding_mode !== 'none' && ` → $${final.toFixed(2)}`}
                              </p>
                            );
                          })()}
                        </div>

                        {/* Options Row */}
                        <div className="flex items-center gap-4 pt-1">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <Switch
                              checked={rule.is_active}
                              onCheckedChange={(v) => {
                                updateLocalRule(rule.id, { is_active: v });
                                handleUpdateRule(rule.id, { is_active: v });
                              }}
                              className="scale-75"
                            />
                            <span className="text-[10px] text-muted-foreground">Active</span>
                          </label>
                        </div>

                        {/* Notes */}
                        <div>
                          <Label className="text-[10px] text-muted-foreground uppercase">Notes</Label>
                          <Textarea
                            value={rule.notes || ''}
                            onChange={e => updateLocalRule(rule.id, { notes: e.target.value })}
                            onBlur={() => handleUpdateRule(rule.id, { notes: rule.notes })}
                            placeholder="Optional notes..."
                            className="dark-input text-xs min-h-[40px] resize-none"
                          />
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-1">
                          <div className="flex gap-1">
                            <Button
                              size="sm" variant="ghost"
                              className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                              onClick={() => handleMoveRule(rule.id, 'up')}
                              disabled={idx === 0}
                            >
                              Move Up
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                              onClick={() => handleMoveRule(rule.id, 'down')}
                              disabled={idx === rules.length - 1}
                            >
                              Move Down
                            </Button>
                          </div>
                          <div className="flex gap-1">
                            {/* Save as Preset */}
                            <Button
                              size="sm" variant="ghost"
                              className="h-6 text-[10px] px-2 text-violet-400 hover:text-violet-300 hover:bg-violet-900/20"
                              onClick={() => {
                                setSavingPresetRuleId(rule.id);
                                setPresetSaveName(rule.rule_name);
                                setShowSavePresetDialog(true);
                              }}
                              disabled={saving}
                            >
                              <BookMarked className="w-3 h-3 mr-1" /> Save as Preset
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="h-6 text-[10px] px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                              onClick={() => handleDeleteRule(rule.id)}
                              disabled={saving}
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Rule Buttons */}
          <div className="flex gap-2 mt-2">
            <Button
              size="sm" variant="outline"
              onClick={handleAddRule}
              disabled={saving}
              className="flex-1 h-7 text-xs border-border border-dashed hover:border-emerald-500/50 hover:text-emerald-400"
            >
              <Plus className="w-3 h-3 mr-1" /> Add Margin Rule
            </Button>
            {presets.length > 0 && (
              <Button
                size="sm" variant="outline"
                onClick={() => setShowPresetPicker(true)}
                disabled={saving}
                className="h-7 text-xs border-violet-700/40 text-violet-400 hover:bg-violet-900/20 hover:text-violet-300 hover:border-violet-500/50"
              >
                <Download className="w-3 h-3 mr-1" /> Load Preset
              </Button>
            )}
          </div>

          {/* Summary */}
          {rules.length > 0 && (
            <div className="mt-2 pt-2 border-t border-border/40 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
              <span>{activeCount} active</span>
              <span className="text-border">|</span>
              <span>{rules.filter(r => r.margin_type === 'percentage').length} percentage</span>
              <span className="text-border">|</span>
              <span>{rules.filter(r => r.margin_type === 'fixed_amount').length} fixed</span>
              {rules.filter(r => r.rounding_mode !== 'none').length > 0 && (
                <>
                  <span className="text-border">|</span>
                  <span className="text-purple-400">{rules.filter(r => r.rounding_mode !== 'none').length} with rounding</span>
                </>
              )}
              {rules.filter(r => r.target_scope !== 'all_rows').length > 0 && (
                <>
                  <span className="text-border">|</span>
                  <span className="text-orange-400">{rules.filter(r => r.target_scope !== 'all_rows').length} targeted</span>
                </>
              )}
            </div>
          )}

          {/* ─── Rules from Other Templates ─── */}
          {otherTemplateRules.length > 0 && (
            <div className="mt-3 pt-2 border-t border-border/40">
              <button
                onClick={() => setShowOtherRules(!showOtherRules)}
                className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors w-full"
              >
                {showOtherRules ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <TrendingUp className="w-3 h-3 text-blue-400" />
                <span className="font-medium">Rules from Other Templates</span>
                <Badge className="bg-blue-900/30 text-blue-400 border-blue-700/40 text-[9px] px-1 py-0 ml-1">
                  {otherTemplateRules.reduce((sum, g) => sum + g.rules.length, 0)}
                </Badge>
              </button>
              {showOtherRules && (
                <div className="mt-2 space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {otherTemplateRules.map(group => (
                    <div key={group.templateId} className="border border-blue-700/20 rounded-md bg-blue-900/5">
                      <div className="px-2 py-1.5 flex items-center justify-between">
                        <span className="text-[10px] font-medium text-blue-400 truncate">{group.templateName}</span>
                        <Badge className="bg-secondary text-muted-foreground text-[9px] px-1 py-0">{group.rules.length} rule{group.rules.length !== 1 ? 's' : ''}</Badge>
                      </div>
                      <div className="px-2 pb-1.5 space-y-0.5">
                        {group.rules.map(rule => (
                          <div key={rule.id} className="flex items-center gap-1.5 text-[9px] text-muted-foreground/80 bg-secondary/20 rounded px-1.5 py-1">
                            <span className="truncate flex-1">{rule.rule_name}</span>
                            {rule.margin_type === 'percentage' ? (
                              <span className="text-blue-400 flex-shrink-0">{rule.margin_value >= 0 ? '+' : ''}{rule.margin_value}%</span>
                            ) : (
                              <span className="text-green-400 flex-shrink-0">{rule.margin_value >= 0 ? '+' : ''}${rule.margin_value}</span>
                            )}
                            <span className="text-muted-foreground/50 flex-shrink-0">{rule.applies_to_field}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}

      {/* ─── Save as Preset Dialog ─── */}
      <Dialog open={showSavePresetDialog} onOpenChange={setShowSavePresetDialog}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <BookMarked className="w-5 h-5 text-violet-400" />
              Save as Preset
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Save this margin rule configuration to the preset library for reuse across templates.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Preset Name</Label>
              <Input
                value={presetSaveName}
                onChange={e => setPresetSaveName(e.target.value)}
                placeholder="e.g. ACC 10% Uplift"
                className="dark-input text-xs"
              />
            </div>
            {savingPresetRuleId && (() => {
              const rule = rules.find(r => r.id === savingPresetRuleId);
              if (!rule) return null;
              return (
                <div className="bg-secondary/30 border border-border/40 rounded px-3 py-2 space-y-1">
                  <p className="text-[10px] text-muted-foreground/70">Rule Configuration</p>
                  <div className="text-[10px] text-foreground space-y-0.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Field:</span>
                      <span>{rule.applies_to_field}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type:</span>
                      <span>{rule.margin_type === 'percentage' ? `${rule.margin_value}%` : `$${rule.margin_value}`}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rounding:</span>
                      <span>{rule.rounding_mode !== 'none' ? `${rule.rounding_mode} to $${rule.rounding_unit}` : 'None'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Scope:</span>
                      <span>{rule.target_scope}{rule.target_config?.value ? ` = ${rule.target_config.value}` : ''}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSavePresetDialog(false)} className="text-muted-foreground">Cancel</Button>
            <Button
              onClick={handleSaveAsPreset}
              disabled={saving || !presetSaveName.trim()}
              className="bg-violet-600 hover:bg-violet-500 text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <BookMarked className="w-4 h-4 mr-1" />}
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Load from Preset Dialog ─── */}
      <Dialog open={showPresetPicker} onOpenChange={setShowPresetPicker}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Library className="w-5 h-5 text-violet-400" />
              Margin Rule Presets
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Select a preset to create a new margin rule pre-configured with its settings.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-1.5 min-h-0 pr-1">
            {presetsLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading presets...
              </div>
            ) : presets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-xs">
                <Library className="w-8 h-8 mx-auto mb-2 opacity-20" />
                <p>No presets saved yet.</p>
                <p className="text-[10px] mt-1 opacity-60">Use "Save as Preset" on any margin rule to add it to the library.</p>
              </div>
            ) : (
              presets.map(preset => (
                <div
                  key={preset.id}
                  className="border border-border/60 rounded-md hover:border-violet-500/40 hover:bg-violet-900/5 transition-colors"
                >
                  <div className="px-3 py-2 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-foreground truncate">{preset.rule_name}</span>
                        {preset.margin_type === 'percentage' ? (
                          <Badge className="bg-blue-900/40 text-blue-400 border-blue-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                            <Percent className="w-2.5 h-2.5 mr-0.5" />
                            {preset.margin_value >= 0 ? '+' : ''}{preset.margin_value}%
                          </Badge>
                        ) : (
                          <Badge className="bg-green-900/40 text-green-400 border-green-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                            <DollarSign className="w-2.5 h-2.5 mr-0.5" />
                            {preset.margin_value >= 0 ? '+' : ''}${preset.margin_value}
                          </Badge>
                        )}
                        {preset.target_scope !== 'all_rows' && (
                          <Badge className="bg-orange-900/40 text-orange-400 border-orange-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                            <Target className="w-2.5 h-2.5 mr-0.5" />
                            {preset.target_config?.value || preset.target_scope}
                          </Badge>
                        )}
                        {preset.rounding_mode !== 'none' && (
                          <Badge className="bg-purple-900/40 text-purple-400 border-purple-700/50 text-[9px] px-1 py-0 flex-shrink-0">
                            <CircleDot className="w-2.5 h-2.5 mr-0.5" />
                            ${preset.rounding_unit}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[9px] text-muted-foreground/60">
                        <span>Field: {preset.applies_to_field}</span>
                        {preset.notes && (
                          <>
                            <span className="text-border">|</span>
                            <span className="truncate">{preset.notes}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddFromPreset(preset)}
                        disabled={saving}
                        className="h-7 text-[10px] px-2 border-violet-700/40 text-violet-400 hover:bg-violet-900/20"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Use
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeletePreset(preset.id)}
                        className="h-7 text-[10px] px-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default MarginRulesManager;
